/**
 * Custom Embla Carousel slider element with thumbnails, autoplay, dots, arrows, and responsive features.
 * @extends HTMLElement
 */
class bdSlider extends HTMLElement {
  // -----------------------------
  // STATIC CONSTANTS (frozen for immutability)
  // -----------------------------
  static #BREAKPOINTS = Object.freeze({
    MOBILE: 749,
    TABLET: 1024,
    MOBILE_ONLY: 480,
  });

  static #DISABLE_SETTINGS = Object.freeze(['mobile', 'tablet', 'desktop', 'tablet-up', 'mobile-only']);
  static #EDGE_DISTANCES = Object.freeze({ NEAR: 150, EXTREME: 50 });
  static #DEFAULT_SPEED = 7;
  static #EDGE_CLASSES = Object.freeze([
    'near-left-edge',
    'near-right-edge', 
    'extreme-left-edge',
    'extreme-right-edge',
    'is-center',
  ]);

  // -----------------------------
  // PRIVATE FIELDS
  // -----------------------------
  #resizeTimeout = null;
  #resizeHandler = () => this.#handleResize();
  #wheelHandler = (e) => this.#onWheel(e);
  #dom = {};
  
  // Event cleanup tracking
  #emblaUnsubscribers = [];
  #autoplayListeners = [];
  #thumbClickHandlers = [];
  #dotClickHandlers = [];
  #paginationUnsubscriber = null;
  #thumbSelectUnsubscriber = null;
  #dotsSelectUnsubscriber = null;
  
  // Performance optimizations
  #edgesRafId = null;
  #lastEdgeProgress = null;
  #cachedViewportWidth = 0;
  #cachedDisableState = null;

  embla = null;
  thumbsEmbla = null;
  autoplayId = null;

  // -----------------------------
  // PUBLIC API
  // -----------------------------
  initSlider() {
    this.#init();
  }

  destroySlider() {
    this.#destroy();
  }

  // -----------------------------
  // LIFECYCLE HOOKS
  // -----------------------------
  connectedCallback() {
    window.addEventListener('resize', this.#resizeHandler, { passive: true });
    this.#init();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeHandler);
    this.#destroy();
  }

  // -----------------------------
  // INITIALIZATION
  // -----------------------------
  #init() {
    this.#cacheDom();
    if (!this.#dom.slider) {
      console.warn('bdSlider: .bd-slider not found in the component.');
      return;
    }

    if (this.#shouldDisable()) {
      this.#destroy();
      return;
    }

    this.#initEmbla();
    this.#initThumbnails();
    this.#initAutoplay();
    this.#initDots();
    this.#initArrows();
    this.#initCustomPagination();

    requestAnimationFrame(() => this.#updateControls());
  }

  #cacheDom() {
    this.#dom = {
      slider: this.querySelector('.bd-slider'),
      track: this.querySelector('.bd-track'),
      // Only cache if they exist (dynamically created ones will be null)
      prev: this.querySelector('[data-prev]'),
      next: this.querySelector('[data-next]'),
      dots: this.querySelector('.slider-dots'),
      prevText: this.querySelector('[data-prev-text]'),
      nextText: this.querySelector('[data-next-text]'),
    };
  }

  #shouldDisable() {
    const setting = this.dataset.sliderDisable;
    if (!setting) return false;
    if (!bdSlider.#DISABLE_SETTINGS.includes(setting)) return false;

    // Cache viewport width for this check
    const w = window.innerWidth;
    
    switch (setting) {
      case 'mobile': return w < bdSlider.#BREAKPOINTS.MOBILE;
      case 'tablet': return w >= bdSlider.#BREAKPOINTS.MOBILE && w < bdSlider.#BREAKPOINTS.TABLET;
      case 'desktop': return w >= bdSlider.#BREAKPOINTS.TABLET;
      case 'tablet-up': return w >= bdSlider.#BREAKPOINTS.MOBILE;
      case 'mobile-only': return w < bdSlider.#BREAKPOINTS.MOBILE_ONLY;
      default: return false;
    }
  }

  // -----------------------------
  // EMBLA INITIALIZATION
  // -----------------------------
  #initEmbla() {
    const opts = this.#buildOptions();
    const plugins = this.#buildPlugins();

    this.#cleanupClones();
    this.#assignSlideUIDs();
    this.#duplicateSlidesIfNeeded();

    this.embla = EmblaCarousel(this.#dom.slider, opts, plugins);

    this.dispatchEvent(
      new CustomEvent('embla-ready', {
        bubbles: true,
        detail: { embla: this.embla },
      }),
    );

    const initial = parseInt(this.dataset.initialSlide);
    if (!isNaN(initial)) this.embla.scrollTo(initial, true);

    this.#subscribeToEmblaEvents();
    this.#dom.slider.addEventListener('wheel', this.#wheelHandler, { passive: false });

    this.#markEdges();
    this.#dom.slider.classList.toggle('no-slides', this.#allSlidesFit());
  }

  #buildOptions() {
    const d = this.dataset;
    const fade = d.fade === 'true';

    return {
      loop: d.wrapAround === 'true',
      align: d.align || 'start',
      draggable: !fade,
      dragFree: fade ? false : d.dragFree === 'true',
      containScroll: fade ? false : d.containScroll || false,
      speed: fade ? 0 : parseInt(d.speed ?? bdSlider.#DEFAULT_SPEED),
      skipSnaps: false,
      inViewThreshold: 0.25,
      containerSelector: '.bd-track',
      slides: '.bd-track > *',
    };
  }

  #buildPlugins() {
    const scrollSpeed = parseFloat(this.dataset.scrollSpeed);
    const fade = this.dataset.fade === 'true';

    if (fade) return [EmblaCarouselFade()];
    if (scrollSpeed) {
      return [EmblaCarouselAutoScroll({
        speed: scrollSpeed,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        target: 'snap',
      })];
    }
    return [];
  }

  #assignSlideUIDs() {
    const children = this.#dom.track.children;
    for (let i = 0; i < children.length; i++) {
      children[i].dataset.uid = i;
    }
  }

  #cleanupClones() {
    const clones = this.#dom.track.querySelectorAll('.is-clone');
    for (let i = 0; i < clones.length; i++) {
      clones[i].remove();
    }
  }

  #duplicateSlidesIfNeeded() {
    if (!this.hasAttribute('data-fill-available')) return;

    // Collect originals once
    const originalSlides = [];
    const children = this.#dom.track.children;
    for (let i = 0; i < children.length; i++) {
      if (!children[i].classList.contains('is-clone')) {
        originalSlides.push(children[i]);
      }
    }

    if (!originalSlides.length) return;

    const viewport = this.#dom.slider.offsetWidth;
    
    // Calculate initial width
    let totalWidth = 0;
    for (let i = 0; i < children.length; i++) {
      totalWidth += children[i].offsetWidth;
    }

    const MAX_PASSES = 6;
    let passes = 0;

    // Pre-calculate original slide widths to avoid repeated offsetWidth reads
    const originalWidths = originalSlides.map(s => s.offsetWidth);
    const totalOriginalWidth = originalWidths.reduce((a, b) => a + b, 0);

    while (totalWidth < viewport && passes < MAX_PASSES) {
      for (let i = 0; i < originalSlides.length; i++) {
        const clone = originalSlides[i].cloneNode(true);
        clone.classList.add('is-clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        
        const focusable = clone.querySelectorAll('a, button, input, [tabindex]');
        for (let j = 0; j < focusable.length; j++) {
          focusable[j].setAttribute('tabindex', '-1');
        }

        this.#dom.track.appendChild(clone);
      }
      // Use pre-calculated width instead of re-reading offsetWidth
      totalWidth += totalOriginalWidth;
      passes++;
    }
  }

  #subscribeToEmblaEvents() {
    this.#unsubscribeFromEmblaEvents();

    const events = ['select', 'scroll', 'init', 'reInit', 'resize'];
    
    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      const unsubscriber = this.embla.on(event, () => {
        switch (event) {
          case 'select':
          case 'scroll':
            this.#scheduleMarkEdges();
            break;
          case 'init':
          case 'reInit':
          case 'resize':
            this.#updateControls();
            break;
        }
      });
      this.#emblaUnsubscribers.push(unsubscriber);
    }
  }

  /**
   * Batch edge marking with RAF to prevent excessive DOM updates during scroll
   */
  #scheduleMarkEdges() {
    if (this.#edgesRafId) return;
    this.#edgesRafId = requestAnimationFrame(() => {
      this.#markEdges();
      this.#edgesRafId = null;
    });
  }

 #unsubscribeFromEmblaEvents() {
  for (let i = 0; i < this.#emblaUnsubscribers.length; i++) {
    if (typeof this.#emblaUnsubscribers[i] === 'function') {
      this.#emblaUnsubscribers[i]();
    }
  }
  this.#emblaUnsubscribers = [];

  if (this.#edgesRafId) {
    cancelAnimationFrame(this.#edgesRafId);
    this.#edgesRafId = null;
  }
}

  #allSlidesFit() {
    if (!this.embla) return false;

    const slides = this.embla.slideNodes();
    const viewportWidth = this.#dom.slider.offsetWidth;
    let totalWidth = 0;

    for (let i = 0; i < slides.length; i++) {
      if (!slides[i].classList.contains('is-clone')) {
        totalWidth += slides[i].offsetWidth;
      }
    }

    return totalWidth <= viewportWidth;
  }

  // -----------------------------
  // THUMBNAILS
  // -----------------------------
  #initThumbnails() {
    const selector = this.dataset.sliderThumbs;
    if (!selector) return;

    const root = document.querySelector(selector);
    if (!root) return;

    this.thumbsEmbla?.destroy();
    this.#cleanupThumbHandlers();

    this.thumbsEmbla = EmblaCarousel(root, {
      align: 'start',
      containScroll: 'keepSnaps',
      dragFree: false,
      slides: '.bd-track > *',
    });

    const thumbs = this.thumbsEmbla.slideNodes();
    const emblaRef = this.embla;

    // Store handlers for cleanup
    for (let i = 0; i < thumbs.length; i++) {
      const index = i;
      const handler = () => emblaRef.scrollTo(index, true);
      thumbs[i].addEventListener('click', handler);
      this.#thumbClickHandlers.push({ el: thumbs[i], handler });
    }

    this.#thumbSelectUnsubscriber = this.embla.on('select', () => {
      const index = this.embla.selectedScrollSnap();
      for (let i = 0; i < thumbs.length; i++) {
        thumbs[i].classList.toggle('is-active', i === index);
      }
      this.thumbsEmbla.scrollTo(index, false);
    });
  }

#cleanupThumbHandlers() {
  for (let i = 0; i < this.#thumbClickHandlers.length; i++) {
    this.#thumbClickHandlers[i].el.removeEventListener('click', this.#thumbClickHandlers[i].handler);
  }
  this.#thumbClickHandlers = [];

  if (typeof this.#thumbSelectUnsubscriber === 'function') {
    this.#thumbSelectUnsubscriber();
  }
  this.#thumbSelectUnsubscriber = null;
}

  // -----------------------------
  // AUTOPLAY
  // -----------------------------
  #initAutoplay() {
    const ms = parseInt(this.dataset.autoplay);
    if (this.dataset.scrollSpeed || !ms) return;

    this.#stopAutoplay();
    this.#cleanupAutoplayListeners();

    const stop = () => this.#stopAutoplay();
    const resume = () => this.#startAutoplay(ms);

    // Consolidated listener setup
    const listeners = [
      ['mouseenter', stop],
      ['pointerdown', stop],
      ['mouseleave', resume],
      ['pointerup', resume],
      ['touchstart', stop, { passive: true }],
      ['touchend', resume, { passive: true }],
    ];

    for (let i = 0; i < listeners.length; i++) {
      const [event, handler, options] = listeners[i];
      this.#dom.slider.addEventListener(event, handler, options);
      this.#autoplayListeners.push({ event, handler, options });
    }

    this.#startAutoplay(ms);
  }

  #startAutoplay(ms) {
    this.#stopAutoplay();
    this.autoplayId = setInterval(() => this.embla?.scrollNext(), ms);
  }

  #stopAutoplay() {
    if (this.autoplayId) {
      clearInterval(this.autoplayId);
      this.autoplayId = null;
    }
  }

  #cleanupAutoplayListeners() {
    for (let i = 0; i < this.#autoplayListeners.length; i++) {
      const { event, handler, options } = this.#autoplayListeners[i];
      this.#dom.slider?.removeEventListener(event, handler, options);
    }
    this.#autoplayListeners = [];
  }

  // -----------------------------
  // CONTROLS
  // -----------------------------
  #updateControls() {
    if (!this.embla) return;

    const canScroll = this.embla.canScrollNext() || this.embla.canScrollPrev();
    const display = canScroll ? '' : 'none';

    if (this.#dom.prev) this.#dom.prev.style.display = display;
    if (this.#dom.next) this.#dom.next.style.display = display;
    if (this.#dom.dots) this.#dom.dots.style.display = display;
  }

  // -----------------------------
  // DOTS
  // -----------------------------
  #initDots() {
    this.#cleanupDotHandlers();

    if (this.dataset.dots !== 'true') return;

    if (!this.#dom.dots) {
      this.#dom.dots = document.createElement('div');
      this.#dom.dots.className = 'slider-dots';
      this.#dom.slider.after(this.#dom.dots);
    }

    const originals = this.embla.slideNodes().filter((s) => !s.classList.contains('is-clone'));
    this.#dom.dots.innerHTML = '';

    // Use DocumentFragment for batch DOM insertion
    const fragment = document.createDocumentFragment();
    const emblaRef = this.embla;

    for (let i = 0; i < originals.length; i++) {
      const dot = document.createElement('button');
      dot.className = 'slide-dot';
      dot.type = 'button';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);

      const index = i;
      const handler = () => emblaRef.scrollTo(index);
      dot.addEventListener('click', handler);
      this.#dotClickHandlers.push({ el: dot, handler });

      fragment.appendChild(dot);
    }

    this.#dom.dots.appendChild(fragment);
    this.#dom.dots.firstElementChild?.classList.add('is-selected');

    this.#dotsSelectUnsubscriber = this.embla.on('select', () => {
      const index = this.embla.selectedScrollSnap();
      const dots = this.#dom.dots?.children;
      if (!dots) return;
      
      for (let i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-selected', i === index);
      }
    });
  }

#cleanupDotHandlers() {
  for (let i = 0; i < this.#dotClickHandlers.length; i++) {
    this.#dotClickHandlers[i].el.removeEventListener('click', this.#dotClickHandlers[i].handler);
  }
  this.#dotClickHandlers = [];

  if (typeof this.#dotsSelectUnsubscriber === 'function') {
    this.#dotsSelectUnsubscriber();
  }
  this.#dotsSelectUnsubscriber = null;
}

  // -----------------------------
  // ARROWS
  // -----------------------------
  #onPrevClick = () => this.embla?.scrollPrev();
  #onNextClick = () => this.embla?.scrollNext();

  #initArrows() {
    if (this.dataset.arrows !== 'true') return;

    if (!this.#dom.prev) this.#dom.prev = this.#createArrow('prev');
    if (!this.#dom.next) this.#dom.next = this.#createArrow('next');

    // Remove before adding to prevent duplicates
    this.#dom.prev.removeEventListener('click', this.#onPrevClick);
    this.#dom.next.removeEventListener('click', this.#onNextClick);

    this.#dom.prev.addEventListener('click', this.#onPrevClick);
    this.#dom.next.addEventListener('click', this.#onNextClick);
  }

  #createArrow(type) {
    const btn = document.createElement('button');
    btn.className = `${type} slide-arrow`;
    btn.setAttribute(`data-${type}`, '');
    btn.setAttribute('aria-label', type === 'prev' ? 'Previous slide' : 'Next slide');
    
    // Minified SVG strings
    btn.innerHTML = type === 'prev'
      ? '<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"><path d="M12 15l-5-5 5-5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      : '<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"><path d="M8 5l5 5-5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    this.#dom.slider.appendChild(btn);
    return btn;
  }

  // -----------------------------
  // CUSTOM PAGINATION
  // -----------------------------
 #initCustomPagination() {
  if (!this.#dom.slider.hasAttribute('data-custom-pag')) return;
  if (!this.#dom.prevText || !this.#dom.nextText) return;

  if (typeof this.#paginationUnsubscriber === 'function') {
    this.#paginationUnsubscriber();
    this.#paginationUnsubscriber = null;
  }

  const slides = this.embla.slideNodes();
  const len = slides.length;

  const update = () => {
    const i = this.embla.selectedScrollSnap();
    this.#dom.prevText.textContent = slides[(i - 1 + len) % len]?.dataset.title || '';
    this.#dom.nextText.textContent = slides[(i + 1) % len]?.dataset.title || '';
  };

  this.#paginationUnsubscriber = this.embla.on('select', update);
  update();
}

  // -----------------------------
  // EDGE MARKING
  // -----------------------------
  #markEdges() {
    if (!this.embla) return;

    const progress = this.embla.scrollProgress();
    
    // Skip if progress hasn't changed (deduplication for scroll events)
    if (this.#lastEdgeProgress !== null && 
        Math.abs(progress - this.#lastEdgeProgress) < 0.0005) {
      return;
    }
    this.#lastEdgeProgress = progress;

    const viewport = this.#dom.slider.getBoundingClientRect();
    const snaps = this.embla.scrollSnapList();
    const slides = this.embla.slideNodes();
    const edgeClasses = bdSlider.#EDGE_CLASSES;
    const NEAR = bdSlider.#EDGE_DISTANCES.NEAR;
    const EXTREME = bdSlider.#EDGE_DISTANCES.EXTREME;
    const viewportLeft = viewport.left;
    const viewportRight = viewport.right;

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      slide.classList.remove(...edgeClasses);

      const rect = slide.getBoundingClientRect();
      const center = rect.left + rect.width * 0.5;

      if (Math.abs(snaps[i] - progress) < 0.001) {
        slide.classList.add('is-center');
      }

      const distLeft = center - viewportLeft;
      const distRight = viewportRight - center;

      if (distLeft < EXTREME) {
        slide.classList.add('extreme-left-edge');
      } else if (distRight < EXTREME) {
        slide.classList.add('extreme-right-edge');
      } else if (distLeft < NEAR) {
        slide.classList.add('near-left-edge');
      } else if (distRight < NEAR) {
        slide.classList.add('near-right-edge');
      }
    }
  }

  // -----------------------------
  // WHEEL HANDLING
  // -----------------------------
  #onWheel(e) {
    if (!this.embla) return;
    const deltaX = e.deltaX;
    if (Math.abs(deltaX) < 2) return;
    if (!this.embla.canScrollNext() && !this.embla.canScrollPrev()) return;

    e.preventDefault();
    deltaX > 0 ? this.embla.scrollNext() : this.embla.scrollPrev();
  }

  // -----------------------------
  // RESIZE HANDLING
  // -----------------------------
  #handleResize() {
    clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = setTimeout(() => {
      const currentDisable = this.#shouldDisable();
      const wasInitialized = !!this.embla;

      // Only full reinit when crossing disable threshold
      if (wasInitialized === currentDisable) {
        // Same state - let Embla handle internal resize
        if (this.embla) {
          this.#updateControls();
          this.#markEdges();
        }
        return;
      }

      this.#destroy();
      this.#init();
    }, 250);
  }

  // -----------------------------
  // DESTROY
  // -----------------------------
  #destroy() {
  this.#stopAutoplay();
  this.#cleanupAutoplayListeners();
  this.#cleanupThumbHandlers();
  this.#cleanupDotHandlers();
  this.#unsubscribeFromEmblaEvents();

  if (typeof this.#paginationUnsubscriber === 'function') {
    this.#paginationUnsubscriber();
    this.#paginationUnsubscriber = null;
  }

  this.#dom.prev?.removeEventListener('click', this.#onPrevClick);
  this.#dom.next?.removeEventListener('click', this.#onNextClick);

  this.embla?.destroy();
  this.thumbsEmbla?.destroy();

  this.embla = null;
  this.thumbsEmbla = null;
  this.#lastEdgeProgress = null;

  this.#dom.slider?.removeEventListener('wheel', this.#wheelHandler);
  
  this.#dom.dots?.remove();
  this.#dom.prev?.remove();
  this.#dom.next?.remove();

  this.#dom.dots = null;
  this.#dom.prev = null;
  this.#dom.next = null;
}
}

customElements.define('bd-slider', bdSlider);